// src/services/apiService.js
import { ENDPOINTS, getAuthHeaders } from '../config';

// ============================================================================
// COMPONENTS (Roofs, Walls, Canopies, Accessories)
// ============================================================================

export const getRoofs = async () => {
    const response = await fetch(ENDPOINTS.COMPONENTS.ROOFS, {
        headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch roofs');
    const result = await response.json();
    // Backend wraps data in { data: [...] }, extract the array
    return result.data || result;
};

export const getWalls = async () => {
    const response = await fetch(ENDPOINTS.COMPONENTS.WALLS, {
        headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch walls');
    const result = await response.json();
    // Backend wraps data in { data: [...] }, extract the array
    return result.data || result;
};

export const getCanopies = async () => {
    const response = await fetch(ENDPOINTS.COMPONENTS.CANOPIES, {
        headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch canopies');
    const result = await response.json();
    // Backend wraps data in { data: [...] }, extract the array
    return result.data || result;
};

export const getAccessories = async () => {
    const response = await fetch(ENDPOINTS.ADMIN.ACCESSORIES, {
        headers: getAuthHeaders() 
    });
    if (!response.ok) throw new Error('Failed to fetch accessories');
    return response.json();
};

// ============================================================================
// PRICING (Wall Prices, Base Prices, Accessory Prices)
// ============================================================================

export const getWallPrices = async () => {
    const response = await fetch(ENDPOINTS.ADMIN.WALL_PRICES, {
        headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch wall prices');
    return response.json();
};

export const getBasePrices = async () => {
    const response = await fetch(ENDPOINTS.ADMIN.BASE_PRICES, {
        headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch base prices');
    return response.json();
};

export const updateWallPrice = async (priceId, newPrice) => {
    const response = await fetch(ENDPOINTS.ADMIN.WALL_PRICE(priceId), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify([
            {
                op: 'replace',
                path: '/price',
                value: newPrice
            }
        ])
    });
    
    if (!response.ok) throw new Error('Failed to update wall price');
    return response.json();
};

export const updateBasePrice = async (basePriceId, newPrice) => {
    const response = await fetch(ENDPOINTS.ADMIN.BASE_PRICE(basePriceId), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify([
            {
                op: 'replace',
                path: '/price',
                value: newPrice
            }
        ])
    });
    
    if (!response.ok) throw new Error('Failed to update base price');
    return response.json();
};

export const updateAccessoryPrice = async (accessoryId, newPrice) => {
    const response = await fetch(ENDPOINTS.ADMIN.ACCESSORY(accessoryId), {
        method: 'PATCH',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
        },
        body: newPrice.toString()  
    });
    
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update accessory price: ${errorText}`);
    }
    return response.json();
};

export const createManualQuote = async (data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(ENDPOINTS.MANUAL_QUOTES, {
        method: 'POST',
        headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create manual lead');
    }
    return await response.json();
};
// ============================================================================
// USER MANAGEMENT
// ============================================================================

export const getUsers = async () => {
    const response = await fetch(ENDPOINTS.USER_MANAGEMENT.USERS, {
        headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
};

export const updateUserRole = async (userId, newRole) => {
    // ADMIN role = promote, USER role = demote
    const endpoint = newRole === 'ADMIN' 
        ? ENDPOINTS.USER_MANAGEMENT.PROMOTE(userId)
        : ENDPOINTS.USER_MANAGEMENT.DEMOTE(userId);
    
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders()
    });
    
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update user role: ${errorText}`);
    }
    
    return response.json();
};

export const deleteUser = async (userId) => {

    
    const response = await fetch(`${ENDPOINTS.USER_MANAGEMENT.USERS}/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    
    if (!response.ok) throw new Error('Failed to delete user');
    return response.json();
};

export const inviteUser = async (formData) => {

    const response = await fetch(ENDPOINTS.USER_MANAGEMENT.INVITE_USER, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            password: formData.password
        })
    });
    
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to invite user: ${errorText}`);
    }
    
    return response.json();
};

// ============================================================================
// COMPANY PROFILE
// ============================================================================

export const getCompanyProfile = async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(ENDPOINTS.DEALER.PROFILE, {
        headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    if (!response.ok) throw new Error("Failed to load profile");
    return await response.json();
};

export const updateCompanyProfile = async (id, data) => { 
    const token = localStorage.getItem('authToken');
    const response = await fetch(ENDPOINTS.DEALER.PROFILE, {
        method: 'PUT',
        headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error("Failed to update profile");
    return await response.json();
};

export const uploadDealerLogo = async (file) => {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(ENDPOINTS.DEALER.PROFILE_LOGO, {
        method: 'POST',
        headers: { 
            'Authorization': `Bearer ${token}` 
        },
        body: formData
    });

    if (!response.ok) throw new Error("Failed to upload logo");
    return await response.json();
};

// ============================================================================
// QUOTES (For Dashboard)
// ============================================================================

export const getMyQuotes = async (page = 1, pageSize = 20) => {
    const url = `${ENDPOINTS.DEALER_PORTAL.MY_QUOTES}?page=${page}&pageSize=${pageSize}`;
    
    const response = await fetch(url, {
        headers: getAuthHeaders()
    });
    
    if (!response.ok) throw new Error('Failed to fetch quotes');
    return response.json();
};

export const updateQuoteStatus = async (quoteId, status, internalNotes) => {
    const body = {};
    if (status !== undefined) body.status = status;
    if (internalNotes !== undefined) body.internalNotes = internalNotes;
    
    const response = await fetch(ENDPOINTS.QUOTE_BY_ID(quoteId), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
    });
    
    if (!response.ok) throw new Error('Failed to update quote');
    return response.json();
};

export const deleteQuote = async (quoteId) => {
    const response = await fetch(ENDPOINTS.QUOTE_BY_ID(quoteId), {
        method: 'DELETE',
        headers: getAuthHeaders()
    });
    
    if (!response.ok) throw new Error('Failed to delete quote');
    return response.json();
};